/**********************************************************************
 * Responsive & a11y-friendly prerolls list
 *********************************************************************/
import React, { useEffect, useState } from 'react';
import api        from '../../services/api';
import Loader     from '../Utils/Loader';
import Modal      from '../common/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faToggleOn, faToggleOff, faPlay } from '@fortawesome/free-solid-svg-icons';
import '../../css/prerollsList.css';

const placeholder = '/img/placeholder-cover.jpg';
const getSrc = r => (r.mediaType === 'video' ? r.url : r.url || `/uploads/audio/${r.filename}`);

export default function PrerollsList({ mediaType, onEdit, onChange, refreshKey }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [preview, setPreview] = useState(null);

  /* ─ fetch once per tab or refreshKey ─ */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/prerolls');
        setRows(data.filter(r => r.mediaType === mediaType));
      } finally { setLoading(false); }
    })();
  }, [mediaType, refreshKey]);

  if (loading) return <Loader />;

  /* ─ helpers ─ */
  const handleDelete = async id => {
    if (!window.confirm('Vuoi eliminare questo preroll?')) return;
    await api.delete(`/prerolls/${id}`);
    onChange();
  };

  const handleToggle = async r => {
    await api.put(`/prerolls/${r.id}`, { enabled: !r.enabled });
    onChange();
  };

  const filtered = rows.filter(r =>
    r.filename?.toLowerCase().includes(search.toLowerCase())
  );

  /* ─ render ─ */
  return (
    <div className="preroll-list">
      {/* search */}
      <div className="preroll-list-search">
        <input
          type="text"
          className="preroll-search"
          placeholder="Cerca filename…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* table / cards */}
      <div className="preroll-list-items" aria-live="polite">
        {/* header  */}
        <div className="preroll-row head" role="row">
          <span>ID</span><span>Nome file</span>
          <span>Dal</span><span>Al</span>
          <span>Cover</span><span>Tipo</span><span>Ordine</span>
          <span className="actions">Azioni</span>
        </div>

        {filtered.map(r => {
          const nice = r.filename?.split('-').slice(1).join('-') || r.filename;
          return (
            <div key={r.id} className="preroll-row body" role="row">
              <span data-label="ID">{r.id}</span>
              <span className="filename" data-label="Nome">{nice}</span>
              <span data-label="Dal">
                {r.startTime ? new Date(r.startTime).toLocaleString('it-IT') : '—'}
              </span>
              <span data-label="Al">
                {r.endTime ? new Date(r.endTime).toLocaleString('it-IT') : '—'}
              </span>

              <div className="cover" data-label="Cover">
                <img
                  src={r.coverUrl || placeholder}
                  alt={`cover ${nice}`}
                  loading="lazy"
                  onClick={() => setPreview(r)}
                  tabIndex={0}
                />
              </div>

              <span data-label="Tipo">{r.type}</span>
              <span data-label="Ordine">{r.prio}</span>

              <div className="actions" data-label="Azioni">
                <button className="preview" title="Anteprima" onClick={() => setPreview(r)}>
                  <FontAwesomeIcon icon={faPlay} />
                </button>
                <button className="edit" title="Modifica" onClick={() => onEdit(r)}>
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button className="delete" title="Elimina" onClick={() => handleDelete(r.id)}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
                <button className="toggle" title="Attiva/Disattiva" onClick={() => handleToggle(r)}>
                  <FontAwesomeIcon icon={r.enabled ? faToggleOn : faToggleOff} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* preview */}
      <Modal open={Boolean(preview)} onClose={() => setPreview(null)}>
        {preview && (
          <>
            <h3 style={{ marginTop: 0 }}>{preview.filename}</h3>
            {preview.mediaType === 'video'
              ? <video src={getSrc(preview)} controls style={{ maxWidth: '100%', maxHeight: '60vh' }}/>
              : <audio src={getSrc(preview)} controls style={{ width: '100%' }}/>
            }
          </>
        )}
      </Modal>
    </div>
  );
}
